import {Controller} from "@hotwired/stimulus"
import {useClickOutside} from "stimulus-use"

const iconMap = {
  success: {class: "success", icon: "fa-check"},
  error: {class: "error", icon: "fa-times"},
  alert: {class: "error", icon: "fa-times"},
  warning: {class: "warning", icon: "fa-exclamation-triangle"},
  notice: {class: "info",  icon: "fa-info"},
  undo: {class: "info",  icon: "fa-info"}
}

class ToastController extends Controller {
  static targets = ["alert"]
  static values = {
    duration: {type: Number, default: 5000}
  }
  // Initialize timeouts even if _activateToast runs before connect()
  // This can happen if a toast is triggered early or controller connects late
  timeouts = new Set()

  connect() {
    useClickOutside(this)
  }

  disconnect() {
    this.timeouts.forEach((timeout) => clearTimeout(timeout))
    this.timeouts.clear()
  }

  alertTargetConnected(element) {
    const duration = this._getToastDuration(element)
    this._handleUndoStacking(element)
    this._activateToast(element, duration)
  }

  addToast(event) {
    const {
      title,
      type = "notice",
      subtitle = "",
      duration
    } = event.detail
    const toast = this._createToastElement(
      title,
      type,
      subtitle,
      duration
    )
    this.element.prepend(toast)
  }

  resumeTimer() {
    this.alertTargets.forEach((toast) => {
      if (toast._userPaused) {
        toast._userPaused = false
        this._startTimer(toast)
      }
    })
  }

  dismiss(event) {
    event.stopPropagation()
    const toast = event.currentTarget.closest("[data-toast-target='alert']")
    this._clearTimeout(toast)
    this._removeToast(toast)
  }

  _activateToast(toast, duration) {
    toast.classList.add("active")
    if (!duration) return

    this._initTimer(toast, duration)
  }

  _initTimer(toast, duration) {
    toast._userPaused = false
    toast.addEventListener("mouseenter", () => {this._pauseTimer(toast)})
    toast.addEventListener("mouseleave", () => {
      if (!toast._userPaused) this._startTimer(toast)
    })

    toast._isPaused = true
    toast._duration = duration
    toast._remainingDuration = duration
    this._startTimer(toast)
  }

  _startTimer(toast) {
    if (!toast._isPaused || toast._remainingDuration <= 0) return

    toast._isPaused = false
    toast._startTime = performance.now()
    const timeout = setTimeout(() => {
      this._removeToast(toast)
    }, toast._remainingDuration)
    toast._timeout = timeout
    this.timeouts.add(timeout)
    this._updateTimerAnimation(toast)
  }

  _updateTimerAnimation(toast) {
    const bar = toast.querySelector("[data-toast-target='bar']")
    bar.style.transition = `transform ${toast._remainingDuration}ms linear`
    requestAnimationFrame(() => {bar.style.transform = "scaleX(0)"})
  }

  _pauseTimer(toast) {
    if (toast._isPaused) return

    toast._isPaused = true
    this._clearTimeout(toast)
    const now = performance.now()
    const elapsed = now - toast._startTime
    toast._remainingDuration = Math.max(0, toast._remainingDuration - elapsed)
    const bar = toast.querySelector("[data-toast-target='bar']")
    bar.style.transition = "none"
    // Calculate how much of the original duration is left
    const currentScale = Math.max(
      0, toast._remainingDuration / toast._duration
    )
    bar.style.transform = `scaleX(${currentScale})`
  }

  _iconData(type) {
    return iconMap[type] || iconMap.notice
  }

  _removeToast(toast) {
    this._clearTimeout(toast)
    toast.classList.remove("active")
    setTimeout(() => {toast.remove()}, 300)
  }

  _clearTimeout(toast) {
    if (toast && toast._timeout) {
      clearTimeout(toast._timeout)
      this.timeouts.delete(toast._timeout)
      toast._timeout = null
    }
  }

  _handleUndoStacking(node) {
    // Only show one undo toast at a time
    if (node.classList.contains("undo")) {
      this.alertTargets.forEach((toast) => {
        if (toast !== node && toast.classList.contains("undo")) {
          this._removeToast(toast)
        }
      })
    }
  }

  _getToastDuration(node) {
    const duration = node.dataset.toastDurationValue
    if (duration === "false") return false

    return duration ? parseInt(duration, 10) : this.durationValue
  }

  _createToastElement(title, type, subtitle, duration) {
    const {class: iconClass, icon} = this._iconData(type)
    const toast = document.createElement("div")
    toast.className = `toast ${type}`
    toast.setAttribute("data-toast-target", "alert")
    if (duration != null) {
      toast.setAttribute("data-toast-duration-value", duration)
    }
    // WARNING: This uses innerHTML with unsanitized content.
    // Currently safe because title/subtitle come from system events,
    // not user input. If user-controlled data is ever passed here,
    // implement sanitization to prevent XSS attacks.
    toast.innerHTML = `
      <div class="content">
        <div class="icon icon--${iconClass}">
          <svg class="fa ${icon}">
            <use href="${window.gon.iconsPath}#${icon}"></use>
          </svg>
        </div>
        <div class="text">
          <div class="title">${title}</div>
          ${subtitle ? `<div class="subtitle">${subtitle}</div>` : ""}
        </div>
      </div>
      ${duration !== false ?
        `<div class="timer-bar ${type}" data-toast-target="bar"></div>` :
        ""}
      <button class="close" data-action="click->toast#dismiss">
        <svg class="fa fa-close">
          <use href="${window.gon.iconsPath}#fa-close"></use>
        </svg>
      </button>
    `
    return toast
  }
}

export default ToastController
